/**
 * FileMakerHelpers.ts - Utility functions for FileMaker integration
 * 
 * CHANGE HISTORY:
 * - 251108_1700 [pending] danielbeer: Consolidated FM helpers from fmwvtool-v4
 * 
 * Provides:
 * - Environment detection
 * - Script calling with standard parameter format
 * - Modifier key handling
 * - Configuration management
 */

import { getFileMakerBridge } from './FileMakerBridge';
import type { FileMakerEnvironment } from './FileMakerBridge';

export interface FileMakerConfig {
  isFileMaker: boolean;
  scriptName: string;
  environment: FileMakerEnvironment;
}

export interface FileMakerScriptParams {
  DAPI_params?: string | object;
  subscript?: string;
  subscript_params?: string | object;
  shift?: boolean;
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  [key: string]: any; // Allow additional custom parameters
}

export interface ModifierKeys {
  shift: boolean;
  alt: boolean;
  ctrl: boolean;
  meta: boolean;
}

/**
 * Detect FileMaker environment and return configuration
 * 
 * @param defaultScriptName - Default script name to use (default: 'jsRunScript')
 * @returns FileMaker configuration object
 */
export function detectFileMaker(defaultScriptName: string = 'jsRunScript'): FileMakerConfig {
  const bridge = getFileMakerBridge();
  const isFileMaker = bridge.isFileMaker();
  
  if (isFileMaker) {
    const env = bridge.getEnvironment();
    
    // Check URL for custom script name
    const urlParams = new URLSearchParams(window.location.search);
    const scriptName = urlParams.get('fmScript') || defaultScriptName;
    
    return {
      isFileMaker: true,
      scriptName,
      environment: env
    };
  }
  
  return {
    isFileMaker: false,
    scriptName: defaultScriptName,
    environment: {
      platform: 'unknown',
      version: '',
      isFileMaker: false,
      isWebDirect: false
    }
  };
}

/**
 * Call FileMaker script with standardized parameter format
 * 
 * Parameters are URL-encoded and formatted for the standard jsRunScript pattern:
 * {
 *   "DAPI_params": "...",
 *   "subscript": "script_name",
 *   "subscript_params": {...},
 *   "shift": false,
 *   "alt": false,
 *   "ctrl": false,
 *   "meta": false
 * }
 * 
 * @param scriptName - Name of FileMaker script to call (optional, uses config default)
 * @param params - Parameters in standard format
 * @param modifierKeys - Current modifier key state (optional)
 * @returns true if script was called, false if not in FileMaker
 */
export function callFMScript(
  scriptName?: string,
  params?: FileMakerScriptParams,
  modifierKeys?: ModifierKeys
): boolean {
  const fmConfig = detectFileMaker();
  
  if (!fmConfig.isFileMaker) {
    console.log('[Integratr] Not in FileMaker - script call ignored');
    return false;
  }

  const bridge = getFileMakerBridge();
  const script = scriptName || fmConfig.scriptName;

  // Build parameter object in expected format
  const scriptParams: FileMakerScriptParams = {
    ...params,
    // Add modifier keys if provided
    ...(modifierKeys && {
      shift: modifierKeys.shift || false,
      alt: modifierKeys.alt || false,
      ctrl: modifierKeys.ctrl || false,
      meta: modifierKeys.meta || false
    })
  };

  // Convert to URL-encoded JSON string (as expected by FM script)
  const jsonString = JSON.stringify(scriptParams);
  const urlEncoded = encodeURIComponent(jsonString);

  bridge.callScript(script, urlEncoded);
  
  console.log(`[Integratr] Called FM script: ${script}`, scriptParams);
  return true;
}

/**
 * Call a FileMaker subscript via the main script
 * 
 * This uses the standard pattern where a main script (jsRunScript) routes
 * to subscripts based on the "subscript" parameter.
 * 
 * @param subscript - Name of subscript to call
 * @param subscriptParams - Parameters for the subscript
 * @param options - Additional options (DAPI params, modifier keys, main script name)
 * @returns true if script was called, false if not in FileMaker
 */
export function callFMSubscript(
  subscript: string,
  subscriptParams?: any,
  options?: {
    DAPI_params?: any;
    modifierKeys?: ModifierKeys;
    mainScript?: string;
  }
): boolean {
  return callFMScript(
    options?.mainScript,
    {
      subscript,
      subscript_params: subscriptParams,
      DAPI_params: options?.DAPI_params
    },
    options?.modifierKeys
  );
}

/**
 * Get current modifier key state from event
 * 
 * @param event - Mouse or keyboard event
 * @returns Modifier key state object
 */
export function getModifierKeys(event?: MouseEvent | KeyboardEvent): ModifierKeys {
  if (!event) {
    return { shift: false, alt: false, ctrl: false, meta: false };
  }

  return {
    shift: event.shiftKey,
    alt: event.altKey,
    ctrl: event.ctrlKey,
    meta: event.metaKey
  };
}

/**
 * React click handler that calls FileMaker with modifier keys
 * 
 * Usage in React components:
 * ```tsx
 * <button onClick={(e) => handleFMClick(e, 'MySubscript', { data: 'value' })}>
 *   Click Me
 * </button>
 * ```
 * 
 * @param event - React mouse event
 * @param subscript - Subscript name to call
 * @param subscriptParams - Parameters for the subscript
 * @param options - Additional options (DAPI params, main script)
 * @returns true if script was called, false if not in FileMaker
 */
export function handleFMClick(
  event: React.MouseEvent,
  subscript: string,
  subscriptParams?: any,
  options?: {
    DAPI_params?: any;
    mainScript?: string;
  }
): boolean {
  const modifierKeys = getModifierKeys(event.nativeEvent);
  
  return callFMSubscript(subscript, subscriptParams, {
    ...options,
    modifierKeys
  });
}

/**
 * Show FileMaker detection banner in console
 * 
 * @returns Banner string
 */
export function showFileMakerBanner(): string {
  const config = detectFileMaker();
  
  if (config.isFileMaker) {
    const banner = `🔗 Running in FileMaker ${config.environment.version} (${config.environment.platform})`;
    console.log(`%c${banner}`, 'background: #10b981; color: white; padding: 4px 8px; border-radius: 4px;');
    return banner;
  }
  
  const banner = '🌐 Running in Web Browser';
  console.log(`%c${banner}`, 'background: #3b82f6; color: white; padding: 4px 8px; border-radius: 4px;');
  return banner;
}

/**
 * Register FileMaker callback handler
 * 
 * This allows FileMaker scripts to call back into the WebViewer with results.
 * 
 * @param callbackName - Name of the callback function (default: 'fmCallback')
 * @param handler - Function to handle the callback
 */
export function registerFileMakerCallback(
  callbackName: string = 'fmCallback',
  handler: (result: any) => void
): void {
  const config = detectFileMaker();
  
  if (!config.isFileMaker) {
    console.log('[Integratr] Not in FileMaker - callback registration ignored');
    return;
  }
  
  // Register global callback function
  (window as any)[callbackName] = (result: any) => {
    try {
      const parsed = typeof result === 'string' ? JSON.parse(result) : result;
      handler(parsed);
    } catch (e) {
      console.error('[Integratr] Failed to parse callback result:', e);
      handler(result);
    }
  };
  
  console.log(`[Integratr] Registered FileMaker callback: ${callbackName}`);
}

/**
 * Call FileMaker script with callback support
 * 
 * This is a convenience wrapper that combines callFMScript with callback registration.
 * 
 * @param scriptName - Name of FileMaker script to call
 * @param params - Parameters for the script
 * @param options - Options including callback handler
 * @returns Promise that resolves with the callback result
 */
export function callFMScriptWithCallback(
  scriptName: string,
  params?: FileMakerScriptParams,
  options?: {
    timeout?: number;
    callbackName?: string;
  }
): Promise<any> {
  return new Promise((resolve, reject) => {
    const config = detectFileMaker();
    
    if (!config.isFileMaker) {
      reject(new Error('Not in FileMaker environment'));
      return;
    }
    
    const callbackName = options?.callbackName || `fmCallback_${Date.now()}`;
    const timeout = options?.timeout || 30000;
    
    // Register callback
    registerFileMakerCallback(callbackName, (result) => {
      // Clean up
      delete (window as any)[callbackName];
      resolve(result);
    });
    
    // Set timeout
    const timeoutId = setTimeout(() => {
      delete (window as any)[callbackName];
      reject(new Error('FileMaker callback timeout'));
    }, timeout);
    
    // Call script with callback parameter
    const success = callFMScript(scriptName, {
      ...params,
      _callback: callbackName
    });
    
    if (!success) {
      clearTimeout(timeoutId);
      delete (window as any)[callbackName];
      reject(new Error('Failed to call FileMaker script'));
    }
  });
}
